import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSurvey } from "../../container/context/SurveyContext";
import { submitNps, npsHandle } from "../../redux/action";
import "./SurveyModal.scss";

const EMOJI_RATING_TO_NUMBER = {
  very_dissatisfied: 1,
  dissatisfied: 2,
  neutral: 3,
  satisfied: 4,
  very_satisfied: 5,
};

const EMOJI_OPTIONS = [
  { key: "very_dissatisfied", emoji: "😡", label: "Very dissatisfied" },
  { key: "dissatisfied", emoji: "😕", label: "Dissatisfied" },
  { key: "neutral", emoji: "😐", label: "Neutral" },
  { key: "satisfied", emoji: "🙂", label: "Satisfied" },
  { key: "very_satisfied", emoji: "😍", label: "Very satisfied" },
];

function FocusTrap({ containerRef, isActive }) {
  useEffect(() => {
    if (!isActive || !containerRef?.current) return;
    const root = containerRef.current;
    const focusables = root.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first) return;
    first.focus();

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener("keydown", handleKeyDown);
    return () => root.removeEventListener("keydown", handleKeyDown);
  }, [isActive, containerRef]);
  return null;
}

function buildNpsPayload(answers) {
  return [
    {
      question: "recommendation",
      answer: answers.recommendScore ?? 0,
      comments: answers?.recommendFeedback?.trim() ?? "",
    },
    {
      question: "service",
      answer: EMOJI_RATING_TO_NUMBER[answers.serviceRating] ?? 0,
      comments: answers.serviceFeedback?.trim() ?? "",
    },
    {
      question: "platform",
      answer: EMOJI_RATING_TO_NUMBER[answers.platformRating] ?? 0,
      comments: answers.platformFeedback?.trim() ?? "",
    },
    { question: "comment", answer: answers.suggestions?.trim() || "" },
  ];
}

export default function SurveyModal() {
  const dispatch = useDispatch();
  const {
    isOpen,
    closeSurvey,
    step,
    answers,
    setAnswer,
    nextStep,
    previousStep,
    canGoNext,
    resetSurvey,
  } = useSurvey();

  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);
  const productData = useSelector((state) => state?.Profile?.productData);
  const productId =
    productData?.find(
      (p) =>
        (p?.status === "Subscribed" || p?.status === "Expired") &&
        p?.subdomain === "InCScreen",
    )?._id ?? null;

  const handleSubmitSurvey = () => {
    const apiPayload = buildNpsPayload(answers);
    setSubmitting(true);
    dispatch(
      submitNps({
        payload: { productId, responses: apiPayload },
        onSuccess: () => {
          resetSurvey();
          setSubmitting(false);
        },
        onFailure: () => setSubmitting(false),
      }),
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const progressPercent = (step / 4) * 100;

  const content = (
    <div
      className={`surveyOverlay ${isOpen ? "surveyOverlayOpen" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="survey-title"
      aria-describedby="survey-subtitle"
    >
      <div ref={modalRef} className="surveyModal">
        <FocusTrap containerRef={modalRef} isActive={isOpen} />
        <header className="surveyHeader" style={{ position: "relative" }}>
          <button
            type="button"
            className="surveyClose"
            onClick={() => {
              dispatch(npsHandle({ action: "SKIP", productId }));
              closeSurvey();
            }}
            aria-label="Close survey"
          >
            ×
          </button>
          <h2 id="survey-title" className="surveyTitle">
            We’d ❤️ your feedback
          </h2>
          <p id="survey-subtitle" className="surveySubtitle">
            This helps us improve your experience
          </p>
        </header>

        <div className="surveyBody">
          {step === 1 && (
            <>
              <p className="surveyQuestion">
                How likely are you to recommend InCruiter to another company?
              </p>
              <div className="surveyScaleRow">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`surveyScaleBtn ${answers.recommendScore === n ? "surveyScaleBtnSelected" : ""}`}
                    onClick={() => setAnswer("recommendScore", n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="surveyScaleLabels">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>
              <div className="surveyTextareaWrapper">
                <textarea
                  className="surveyTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.recommendFeedback}
                  onChange={(e) =>
                    setAnswer("recommendFeedback", e.target.value.slice(0, 500))
                  }
                  maxLength={500}
                  aria-label="Recommendation feedback (500 characters max)"
                />
                <span
                  className={`surveyCharCount ${(answers.recommendFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.recommendFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="surveyQuestion">
                How likely are you to rate our service and coordination support?
              </p>
              <div className="surveyEmojiRow">
                {EMOJI_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`surveyEmojiCard ${answers.serviceRating === opt.key ? "surveyEmojiCardSelected" : ""}`}
                    onClick={() => setAnswer("serviceRating", opt.key)}
                  >
                    <span className="surveyEmoji">{opt.emoji}</span>
                  </button>
                ))}
              </div>
              <div className="surveyScaleLabels2">
                <span>Very dissatisfied</span>
                <span aria-hidden="true"></span>
                <span>Neutral</span>
                <span aria-hidden="true"></span>
                <span>Very satisfied</span>
              </div>
              <div className="surveyTextareaWrapper">
                <textarea
                  className="surveyTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.serviceFeedback}
                  onChange={(e) =>
                    setAnswer("serviceFeedback", e.target.value.slice(0, 500))
                  }
                  maxLength={500}
                  aria-label="Service feedback (500 characters max)"
                />
                <span
                  className={`surveyCharCount ${(answers.serviceFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.serviceFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="surveyQuestion">
                How likely are you to rate your experience with the InCruiter
                platform?
              </p>
              <div className="surveyEmojiRow">
                {EMOJI_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`surveyEmojiCard ${answers.platformRating === opt.key ? "surveyEmojiCardSelected" : ""}`}
                    onClick={() => setAnswer("platformRating", opt.key)}
                  >
                    <span className="surveyEmoji">{opt.emoji}</span>
                  </button>
                ))}
              </div>
              <div className="surveyScaleLabels2">
                <span>Very dissatisfied</span>
                <span aria-hidden="true"></span>
                <span>Neutral</span>
                <span aria-hidden="true"></span>
                <span>Very satisfied</span>
              </div>
              <div className="surveyTextareaWrapper">
                <textarea
                  className="surveyTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.platformFeedback}
                  onChange={(e) =>
                    setAnswer("platformFeedback", e.target.value.slice(0, 500))
                  }
                  maxLength={500}
                  aria-label="Platform feedback (500 characters max)"
                />
                <span
                  className={`surveyCharCount ${(answers.platformFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.platformFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="surveyQuestion">
                Please share any suggestions to improve our platform or
                services.
              </p>
              <div className="surveyTextareaWrapper">
                <textarea
                  className="surveyTextarea2"
                  placeholder="Write your feedback here..."
                  value={answers.suggestions}
                  onChange={(e) =>
                    setAnswer("suggestions", e.target.value.slice(0, 500))
                  }
                  maxLength={500}
                  aria-label="Feedback suggestions (500 characters max)"
                  rows={7}
                />
                <span
                  className={`surveyCharCount ${(answers.suggestions?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.suggestions?.length ?? 0}/500
                </span>
              </div>
            </>
          )}
        </div>

        <footer className="surveyFooter">
          {step > 1 && (
            <button
              type="button"
              className="surveyBtn surveyBtnSecondary"
              onClick={previousStep}
              aria-label="Go back to previous question"
            >
              Back
            </button>
          )}
          <div className="surveyFooterPrimary">
            {step < 4 ? (
              <button
                type="button"
                className="surveyBtn surveyBtnPrimary"
                disabled={!canGoNext}
                onClick={nextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="surveyBtn surveyBtnPrimary"
                disabled={submitting || !canGoNext}
                onClick={handleSubmitSurvey}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
